import { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { SelectField } from "@/components/common";
import { useTran } from "@/hooks";
import { reportingYearStore } from "@/features/reporting-boundary/stores/reporting-year.store";
import { useAuthStore } from "@/features/auth";

const ALL = "__all__";

const FrameworkFilter = observer(({ value, onChange, year }) => {
  const { t } = useTran();
  const { isJJSEA } = useAuthStore();
  const { reportingYears, availableFrameworks } = reportingYearStore;
  const frameworkAllSelectedRef = useRef(false);

  const filteredFrameworks = useMemo(() => {
    const selectedReportingYear =
      year && year !== ALL
        ? reportingYears.find(
            (reportingYear) => String(reportingYear.startYear) === String(year),
          )
        : null;

    const frameworkCodes = selectedReportingYear
      ? (selectedReportingYear.frameworkCodes ?? [])
      : [
          ...new Set(
            reportingYears.flatMap(
              (reportingYear) => reportingYear.frameworkCodes ?? [],
            ),
          ),
        ];

    const frameworkCodeSet = new Set(frameworkCodes);

    return availableFrameworks
      .filter((framework) => frameworkCodeSet.has(framework.code))
      .map((framework) => ({
        code: framework.code,
        name: framework.name ?? framework.code,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [year, reportingYears, availableFrameworks]);

  const options = useMemo(() => {
    const frameworkOptions = filteredFrameworks.map((framework) => ({
      value: framework.code,
      label: framework.name,
    }));

    if (frameworkOptions.length <= 1) {
      return frameworkOptions;
    }

    return [{ value: ALL, label: t("All Frameworks") }, ...frameworkOptions];
  }, [filteredFrameworks, t]);

  useEffect(() => {
    if (value === ALL) {
      frameworkAllSelectedRef.current = true;
      onChange("");
      return;
    }

    if (filteredFrameworks.length === 0) {
      if (value) {
        onChange("");
      }
      return;
    }

    // JJSEA: default to JJ-Group when nothing is selected
    if (!value && isJJSEA && !frameworkAllSelectedRef.current) {
      const jjGroup = filteredFrameworks.find(
        (framework) => framework.code === "JJ-Group",
      );

      if (jjGroup) {
        onChange(jjGroup.code);
        return;
      }
    }

    // Only one framework: auto-select it
    if (filteredFrameworks.length === 1) {
      const onlyFramework = filteredFrameworks[0].code;

      if (value !== onlyFramework) {
        onChange(onlyFramework);
      }
      return;
    }

    // Clear invalid selection
    if (
      value &&
      !filteredFrameworks.some((framework) => framework.code === value)
    ) {
      onChange("");
    }
  }, [filteredFrameworks, value, isJJSEA, onChange]);
  return (
    <SelectField
      label={t("Framework")}
      options={options}
      value={value || ALL}
      onValueChange={(selectedValue) => {
        frameworkAllSelectedRef.current = selectedValue === ALL;
        onChange(selectedValue === ALL ? "" : selectedValue);
      }}
      sortOptions={false}
      placeholder={t("All Frameworks")}
      className="w-full sm:w-44"
    />
  );
});

export default FrameworkFilter;
